import { cpfMask, isValidCPF } from "utils/cpf";

export interface ParseCpfListResult {
  /** Só dígitos, sem máscara, sem repetido — é isso que vai no payload. */
  cpfs: string[];
  error: string | null;
}

/** Teto usado quando o catálogo não declara um. Quem manda é o `spec.max`. */
export const DEFAULT_MAX_CPFS = 10;

const CPF_DIGIT_COUNT = 11;

/** Quantos inválidos a mensagem cita antes de resumir o resto em contagem. */
const MAX_LISTED_ERRORS = 3;

/**
 * Vírgula, ponto-e-vírgula, quebra de linha, tab e espaço.
 *
 * Colar uma coluna de planilha é o caminho real de quem investiga dez CPFs, e
 * planilha não separa por vírgula. Só com vírgula, a colagem inteira virava um
 * único segmento e a tela dizia `CPF inválido: "529.982.247-25 111.444.777-35
 * ..."` — tecnicamente verdade, inútil na prática.
 */
const SEPARATOR = /[\s,;]+/;

/**
 * Valida e normaliza a lista digitada.
 *
 * Não é *a* validação — o backend revalida em `validateReportParams`. O que se
 * ganha aqui é dizer o problema antes do round-trip, apontando qual CPF está
 * errado em vez de um 400 genérico.
 */
export function parseCpfList(
  input: string,
  max: number = DEFAULT_MAX_CPFS
): ParseCpfListResult {
  const segments = input
    .split(SEPARATOR)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  if (segments.length === 0) {
    return { cpfs: [], error: "Informe ao menos um CPF" };
  }

  const invalid: string[] = [];
  const cpfs: string[] = [];
  const seen = new Set<string>();

  for (const segment of segments) {
    const digits = segment.replace(/\D/g, "");
    // 11 dígitos **e** dígito verificador. Só o comprimento deixaria passar
    // `11111111111`, que atravessaria a stack inteira para voltar vazio.
    if (digits.length !== CPF_DIGIT_COUNT || !isValidCPF(digits)) {
      if (!invalid.includes(segment)) invalid.push(segment);
      continue;
    }
    // Repetido não é erro do usuário, é colagem de planilha. Vale uma vez: o
    // mesmo CPF duas vezes consumiria duas vagas do teto e duplicaria a linha
    // no relatório.
    if (seen.has(digits)) continue;
    seen.add(digits);
    cpfs.push(digits);
  }

  // Todos os inválidos de uma vez. Reportar só o primeiro obriga a corrigir,
  // reenviar e descobrir o próximo — dez vezes, no pior caso.
  if (invalid.length > 0) {
    const listed = invalid
      .slice(0, MAX_LISTED_ERRORS)
      .map((segment) => `"${segment}"`)
      .join(", ");
    const rest = invalid.length - MAX_LISTED_ERRORS;
    return {
      cpfs: [],
      error:
        invalid.length === 1
          ? `CPF inválido: ${listed}`
          : `CPFs inválidos: ${listed}${rest > 0 ? ` e mais ${rest}` : ""}`,
    };
  }

  // Depois de deduplicar: colar a mesma planilha duas vezes não deve estourar
  // um teto que os CPFs distintos não estouram.
  if (cpfs.length > max) {
    return {
      cpfs: [],
      error:
        max === 1
          ? "Informe um CPF por vez"
          : `Máximo de ${max} CPFs por consulta (você informou ${cpfs.length})`,
    };
  }

  return { cpfs, error: null };
}

/**
 * A lista de volta em máscara, para o campo. O payload continua indo só com
 * dígitos — a máscara é de leitura, e é o que confirma na tela que a colagem
 * virou N CPFs e não um texto que ninguém conferiu.
 */
export function formatCpfList(cpfs: string[]): string {
  return cpfs.map((cpf) => cpfMask(cpf)).join(", ");
}
